import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, FormControl,
    InputLabel, Select, MenuItem, Chip, Button, Paper,
    FormControlLabel, Checkbox, Alert, Stepper, Step, StepLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    LinearProgress, useTheme, alpha
} from '@mui/material';
import {
    Add as AddIcon,
    DeleteOutline as TrashIcon,
    CheckCircle as CheckIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon,
    Assessment as AssessmentIcon,
    Engineering as EngineeringIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

export default function D5CorrectiveActions({ step, onStepUpdate, stepMapping, stepId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [activeStep, setActiveStep] = useState(0);

    const updateStep = (updates) => {
        onStepUpdate(stepMapping[stepId], { ...step, ...updates });
    };

    const phases = [
        { name: 'Action Design', icon: <AssignmentIcon />, color: '#2196f3' },
        { name: 'Resource Planning', icon: <EngineeringIcon />, color: '#ff9800' },
        { name: 'Risk Assessment', icon: <AssessmentIcon />, color: '#f44336' },
        { name: 'Verification Planning', icon: <CheckIcon />, color: '#9c27b0' }
    ];

    const addCorrectiveAction = () => {
        const newActions = [...(step.correctiveActions || []), {
            id: Date.now(),
            title: '',
            description: '',
            category: '',
            priority: 'medium',
            targetDate: '',
            assignedTo: '',
            department: ''
        }];
        updateStep({ correctiveActions: newActions });
    };

    const updateAction = (index, field, value) => {
        const updatedActions = [...(step.correctiveActions || [])];
        updatedActions[index] = { ...updatedActions[index], [field]: value };
        updateStep({ correctiveActions: updatedActions });
    };

    const deleteAction = (index) => {
        const updatedActions = (step.correctiveActions || []).filter((_, i) => i !== index);
        updateStep({ correctiveActions: updatedActions });
    };

    const addRiskAssessment = () => {
        const newRisks = [...(step.riskAssessments || []), {
            id: Date.now(),
            riskDescription: '',
            probability: 'medium',
            impact: 'medium',
            riskLevel: 'medium',
            mitigation: '',
            contingency: ''
        }];
        updateStep({ riskAssessments: newRisks });
    };

    const updateRisk = (index, field, value) => {
        const updatedRisks = [...(step.riskAssessments || [])];
        updatedRisks[index] = { ...updatedRisks[index], [field]: value };
        
        if (field === 'probability' || field === 'impact') {
            const prob = field === 'probability' ? value : updatedRisks[index].probability;
            const imp = field === 'impact' ? value : updatedRisks[index].impact;
            updatedRisks[index].riskLevel = calculateRiskLevel(prob, imp);
        }
        
        updateStep({ riskAssessments: updatedRisks });
    };

    const calculateRiskLevel = (probability, impact) => {
        const riskMatrix = {
            'low-low': 'low', 'low-medium': 'low', 'low-high': 'medium',
            'medium-low': 'low', 'medium-medium': 'medium', 'medium-high': 'high',
            'high-low': 'medium', 'high-medium': 'high', 'high-high': 'critical'
        };
        return riskMatrix[`${probability}-${impact}`] || 'medium';
    };

    const getRiskColor = (level) => {
        const colors = {
            low: '#4caf50',
            medium: '#ff9800',
            high: '#f44336',
            critical: '#d32f2f'
        };
        return colors[level] || '#ff9800';
    };



    const isActionPlanningComplete = () => {
        const actions = step.correctiveActions || [];
        return actions.length > 0 && actions.every(action => 
            action.title?.trim() && 
            action.description?.trim() && 
            action.rootCauseConnection?.trim() && 
            action.expectedOutcomes?.trim() && 
            action.category && 
            action.priority && 
            action.assignedTo?.trim() && 
            action.department
        );
    };

    const isResourceAllocationComplete = () => {
        return (step.totalBudget !== undefined && step.totalBudget !== null && step.totalBudget !== '') && 
               (step.totalHours !== undefined && step.totalHours !== null && step.totalHours !== '') && 
               step.startDate && 
               step.targetDate && 
               step.personnelRequirements?.trim() &&
               step.equipmentMaterials?.trim() &&
               step.dependencies?.trim();
    };

    const isRiskAssessmentComplete = () => {
        const risks = step.riskAssessments || [];
        return risks.length > 0 && risks.every(risk => 
            risk.riskDescription?.trim() && 
            risk.probability && 
            risk.impact && 
            risk.mitigation?.trim()
        );
    };

    const isVerificationPlanningComplete = () => {
        return step.verificationMethods?.trim() && 
               step.successCriteria?.trim() && 
               step.dataCollectionPlan?.trim() &&
               step.baselineData?.trim();
    };

    // Overall form validation function
    const isFormValid = () => {
        return isActionPlanningComplete() && 
               isResourceAllocationComplete() && 
               isRiskAssessmentComplete() && 
               isVerificationPlanningComplete();
    };

    const renderActionPlanning = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Corrective Action Design (SMART Framework)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Design specific, measurable, achievable actions
                        </Typography>
                    </Box>
                {isActionPlanningComplete() && (
                    <Chip 
                        label="COMPLETED" 
                        color="success" 
                        sx={{ fontWeight: 500 }}
                        icon={<CheckIcon />}
                    />
                )}
            </Box>
            
            <Alert severity={isActionPlanningComplete() ? "success" : "info"} sx={{ mb: 3 }}>
                Design Specific, Measurable, Achievable, Relevant, Time-bound corrective actions based on root cause analysis. Focus on planning what needs to be done, not executing it.
                {!isActionPlanningComplete() && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: At least one action with title, description, root cause connection, expected outcomes, category, priority, assigned person, and department.
                    </Typography>
                )}
            </Alert>

            {(step.correctiveActions || []).map((action, index) => (
                <Paper key={action.id} sx={{ 
                    p: 4, 
                    mb: 4, 
                    backgroundColor: '#f8f9fa',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.15)}`,
                        border: `1px solid ${theme.palette.grey[600]}`
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Action Title"
                                value={action.title}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'title', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Brief, specific title for the corrective action"
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth sx={{ mb: 3 }}>
                                <InputLabel>Action Category</InputLabel>
                                <Select
                                    value={action.category}
                                    onChange={isReadOnly ? undefined : (e) => updateAction(index, 'category', e.target.value)}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Action Category"
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[400],
                                            borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[600],
                                            borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[700],
                                            borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="process_improvement">Process Improvement</MenuItem>
                                    <MenuItem value="training">Training & Development</MenuItem>
                                    <MenuItem value="equipment">Equipment/Technology</MenuItem>
                                    <MenuItem value="documentation">Documentation Update</MenuItem>
                                    <MenuItem value="supplier_management">Supplier Management</MenuItem>
                                    <MenuItem value="quality_control">Quality Control Enhancement</MenuItem>
                                    <MenuItem value="organizational">Organizational Change</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                label="Detailed Action Description"
                                value={action.description}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'description', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Detailed description of what will be done, how it addresses the root cause, and expected outcomes..."
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Root Cause Connection"
                                value={action.rootCauseConnection || ''}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'rootCauseConnection', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Explain how this action specifically addresses the identified root cause..."
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Expected Outcomes & Success Metrics"
                                value={action.expectedOutcomes || ''}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'expectedOutcomes', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Define measurable outcomes and KPIs to track success..."
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Implementation Steps"
                                value={action.implementationSteps || ''}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'implementationSteps', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="List specific steps required to implement this action..."
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Resources Required"
                                value={action.resourcesRequired || ''}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'resourcesRequired', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Detail personnel, equipment, materials, budget needed..."
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Priority Level</InputLabel>
                                <Select
                                    value={action.priority}
                                    onChange={isReadOnly ? undefined : (e) => updateAction(index, 'priority', e.target.value)}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Priority Level"
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[400],
                                            borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[600],
                                            borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[700],
                                            borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="critical">Critical - Immediate</MenuItem>
                                    <MenuItem value="high">High - 1-7 days</MenuItem>
                                    <MenuItem value="medium">Medium - 1-4 weeks</MenuItem>
                                    <MenuItem value="low">Low - 1-3 months</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Assigned To"
                                value={action.assignedTo}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'assignedTo', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Person responsible"
                                sx={{ 
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Department"
                                value={action.department}
                                onChange={isReadOnly ? undefined : (e) => updateAction(index, 'department', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Quality Assurance, Production, etc."
                                sx={{ 
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<TrashIcon />}
                                    onClick={isReadOnly ? undefined : () => deleteAction(index)}
                                    disabled={isReadOnly}
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        opacity: isReadOnly ? 0.6 : 1,
                                        '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                                    }}
                                >
                                    Delete Action
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={isReadOnly ? undefined : addCorrectiveAction}
                    disabled={isReadOnly}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 500,
                        opacity: isReadOnly ? 0.6 : 1,
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                    }}
                >
                    Add Corrective Action
                </Button>
            </Box>
            </CardContent>
        </Card>
    );

    const renderResourceAllocation = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#ff9800', 0.1)}, ${alpha('#ff9800', 0.05)})`,
                        border: `1px solid ${alpha('#ff9800', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <TimelineIcon sx={{ fontSize: 24, color: '#ff9800' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Resource Allocation & Timeline Planning
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Assign resources and set timelines
                        </Typography>
                    </Box>
                {isResourceAllocationComplete() && (
                    <Chip 
                        label="COMPLETED" 
                        color="success" 
                        sx={{ fontWeight: 500 }}
                        icon={<CheckIcon />}
                    />
                )}
            </Box>
            
            <Alert severity={isResourceAllocationComplete() ? "success" : "warning"} sx={{ mb: 3 }}>
                Plan budget, timeline, and resources required for implementation.
                {!isResourceAllocationComplete() && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Budget, total hours, start/target dates, personnel requirements, equipment/materials, and dependencies.
                    </Typography>
                )}
            </Alert>
            
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Total Estimated Budget ($)"
                        value={step.totalBudget || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ totalBudget: e.target.value })}
                        placeholder="0"
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                        InputProps={{
                            readOnly: isReadOnly,
                            startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>$</Typography>
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Estimated Total Hours"
                        value={step.totalHours || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ totalHours: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="0"
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Project Start Date"
                            value={step.startDate ? dayjs(step.startDate) : null}
                            onChange={isReadOnly ? undefined : (newValue) => updateStep({ 
                                startDate: newValue ? newValue.format('YYYY-MM-DD') : null 
                            })}
                            disabled={isReadOnly}
                            format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: { 
                                        mb: 3,
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': { 
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }
                                }
                            }}
                            sx={{ width: '100%', flex: 1 }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Target Completion Date"
                            value={step.targetDate ? dayjs(step.targetDate) : null}
                            onChange={isReadOnly ? undefined : (newValue) => updateStep({ 
                                targetDate: newValue ? newValue.format('YYYY-MM-DD') : null 
                            })}
                            disabled={isReadOnly}
                            format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    sx: { 
                                        mb: 3,
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': { 
                                            borderRadius: 2,
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }
                                }
                            }}
                            sx={{ width: '100%', flex: 1 }}
                        />
                    </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Personnel Requirements"
                        value={step.personnelRequirements || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ personnelRequirements: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Detail specific roles, skills, training levels, number of people needed..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Equipment & Materials"
                        value={step.equipmentMaterials || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ equipmentMaterials: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="List equipment, tools, materials, software, facilities needed..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="External Services & Vendors"
                        value={step.externalServices || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ externalServices: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Detail consultants, contractors, suppliers, testing services..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Training & Development Needs"
                        value={step.trainingNeeds || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ trainingNeeds: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Specify training programs, certifications, skill development required..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Implementation Dependencies"
                        value={step.dependencies || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ dependencies: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="List dependencies on other projects, approvals, regulatory requirements, external factors..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Budget Breakdown & Justification"
                        value={step.budgetBreakdown || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ budgetBreakdown: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Provide detailed cost breakdown including labor, materials, overhead, contingency..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderRiskAssessment = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#f44336', 0.1)}, ${alpha('#f44336', 0.05)})`,
                        border: `1px solid ${alpha('#f44336', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <AssessmentIcon sx={{ fontSize: 24, color: '#f44336' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Implementation Risk Assessment (FMEA Approach)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Assess potential risks in implementation
                        </Typography>
                    </Box>
                {isRiskAssessmentComplete() && (
                    <Chip 
                        label="COMPLETED" 
                        color="success" 
                        sx={{ fontWeight: 500 }}
                        icon={<CheckIcon />}
                    />
                )}
            </Box>
            
            <Alert severity={isRiskAssessmentComplete() ? "success" : "warning"} sx={{ mb: 3 }}>
                Assess risks that could impact the successful implementation of corrective actions.
                {!isRiskAssessmentComplete() && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: At least one risk with description, probability, impact, and mitigation strategy.
                    </Typography>
                )}
            </Alert>

            {(step.riskAssessments || []).map((risk, index) => (
                <Paper key={risk.id} sx={{ 
                    p: 3, 
                    mb: 3, 
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.15)}`,
                        border: `1px solid ${theme.palette.grey[600]}`
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Risk Description"
                                value={risk.riskDescription}
                                onChange={isReadOnly ? undefined : (e) => updateRisk(index, 'riskDescription', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="What could go wrong during implementation? Be specific about the risk scenario..."
                                sx={{ 
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Risk Category & Type"
                                value={risk.riskCategory || ''}
                                onChange={isReadOnly ? undefined : (e) => updateRisk(index, 'riskCategory', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Technical, operational, regulatory, financial, timeline, resource..."
                                sx={{ 
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Potential Impact Details"
                                value={risk.impactDetails || ''}
                                onChange={isReadOnly ? undefined : (e) => updateRisk(index, 'impactDetails', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Describe specific consequences if this risk occurs..."
                                sx={{ 
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Probability</InputLabel>
                                <Select
                                    value={risk.probability}
                                    onChange={isReadOnly ? undefined : (e) => updateRisk(index, 'probability', e.target.value)}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Probability"
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[400],
                                            borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[600],
                                            borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[700],
                                            borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="low">Low (1-3)</MenuItem>
                                    <MenuItem value="medium">Medium (4-6)</MenuItem>
                                    <MenuItem value="high">High (7-10)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Impact</InputLabel>
                                <Select
                                    value={risk.impact}
                                    onChange={isReadOnly ? undefined : (e) => updateRisk(index, 'impact', e.target.value)}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Impact"
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[400],
                                            borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[600],
                                            borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.palette.grey[700],
                                            borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="low">Low (1-3)</MenuItem>
                                    <MenuItem value="medium">Medium (4-6)</MenuItem>
                                    <MenuItem value="high">High (7-10)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Chip
                                label={`Risk: ${risk.riskLevel?.toUpperCase() || 'MEDIUM'}`}
                                sx={{
                                    backgroundColor: getRiskColor(risk.riskLevel),
                                    color: 'white',
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    height: 56,
                                    width: '100%'
                                }}
                            />
                        </Grid>

                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Risk Mitigation Strategy"
                                value={risk.mitigation}
                                onChange={isReadOnly ? undefined : (e) => updateRisk(index, 'mitigation', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Detail specific actions to reduce/prevent this risk, preventive measures..."
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Contingency Response Plan"
                                value={risk.contingency}
                                onChange={isReadOnly ? undefined : (e) => updateRisk(index, 'contingency', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="Step-by-step response plan if this risk materializes..."
                                sx={{ 
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 2 }}>
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<AddIcon />}
                    onClick={isReadOnly ? undefined : addRiskAssessment}
                    disabled={isReadOnly}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 500,
                        opacity: isReadOnly ? 0.6 : 1,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                        }
                    }}
                >
                    Add Risk Assessment
                </Button>
                {(step.riskAssessments || []).length > 0 && (
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<TrashIcon />}
                        onClick={isReadOnly ? undefined : () => {
                            const updatedRisks = (step.riskAssessments || []).slice(0, -1);
                            updateStep({ riskAssessments: updatedRisks });
                        }}
                        disabled={isReadOnly}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            fontWeight: 500,
                            opacity: isReadOnly ? 0.6 : 1,
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                        }}
                    >
                        Delete Last Risk
                    </Button>
                )}
            </Box>
            </CardContent>
        </Card>
    );

        const renderVerificationPlanning = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#9c27b0', 0.1)}, ${alpha('#9c27b0', 0.05)})`,
                        border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <CheckIcon sx={{ fontSize: 24, color: '#9c27b0' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Verification Planning & Measurement Design
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Plan verification and effectiveness measures
                        </Typography>
                    </Box>
                {isVerificationPlanningComplete() && (
                    <Chip 
                        label="COMPLETED" 
                        color="success" 
                        sx={{ fontWeight: 500 }}
                        icon={<CheckIcon />}
                    />
                )}
            </Box>
            
            <Alert severity={isVerificationPlanningComplete() ? "success" : "info"} sx={{ mb: 3 }}>
                Plan how the effectiveness of corrective actions will be verified and measured.
                {!isVerificationPlanningComplete() && (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                        Required: Verification methods, success criteria, data collection plan, and baseline data.
                    </Typography>
                )}
            </Alert>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Verification Methods & KPIs"
                        value={step.verificationMethods || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ verificationMethods: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Define specific measurement methods, KPIs, metrics, data collection procedures..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Success Criteria & Targets"
                        value={step.successCriteria || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ successCriteria: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Define specific, measurable criteria with target values and acceptable ranges..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Data Collection Plan"
                        value={step.dataCollectionPlan || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ dataCollectionPlan: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Detail how, when, where, and by whom data will be collected..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Baseline & Control Data"
                        value={step.baselineData || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ baselineData: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Document pre-implementation baseline data and control group information..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Monitoring Period (days)"
                        value={step.monitoringPeriod || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ monitoringPeriod: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="30"
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={8}>
                    <TextField
                        fullWidth
                        label="Responsible Person/Department"
                        value={step.verificationOwner || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ verificationOwner: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="Who will be responsible for conducting verification..."
                        sx={{ 
                            mb: 3,
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Verification Schedule & Timeline"
                        value={step.verificationSchedule || ''}
                        onChange={isReadOnly ? undefined : (e) => updateStep({ verificationSchedule: e.target.value })}
                        InputProps={{ readOnly: isReadOnly }}
                        placeholder="When verification activities will occur, frequency, review points..."
                        sx={{ 
                            '& .MuiOutlinedInput-root': { 
                                borderRadius: 2,
                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                            }
                        }}
                    />
                </Grid>
            </Grid>
            </CardContent>
        </Card>
    );

    const renderPhase = () => {
        switch (activeStep) {
            case 0: return renderActionPlanning();
            case 1: return renderResourceAllocation();
            case 2: return renderRiskAssessment();
            case 3: return renderVerificationPlanning();
            default: return null;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 4, 
                backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                borderRadius: 4, 
                border: `1px solid ${theme.palette.warning.main}`, 
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <EngineeringIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.warning.dark, 
                            mb: 0.5
                        }}>
                            D5: Corrective Actions Planning & Design
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Systematically design and plan corrective actions using industry-standard CAPA methodologies. Focus on what needs to be done, who will do it, and how effectiveness will be measured.
                        </Typography>
                    </Box>
                </Box>
                
                <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
                    {phases.map((phase, index) => (
                        <Step key={phase.name} completed={
                            index === 0 ? isActionPlanningComplete() :
                            index === 1 ? isResourceAllocationComplete() :
                            index === 2 ? isRiskAssessmentComplete() :
                            isVerificationPlanningComplete()
                        }>
                            <StepLabel 
                                onClick={() => setActiveStep(index)}
                                sx={{ cursor: 'pointer' }}
                            >
                                {phase.name}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderPhase()}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button 
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={() => setActiveStep(prev => prev - 1)}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            },
                            '&.Mui-disabled': {
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[400]
                            }
                        }}
                    >
                        ← Previous Phase
                    </Button>
                    <Button 
                        variant="contained"
                        disabled={activeStep === phases.length - 1}
                        onClick={() => setActiveStep(prev => prev + 1)}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: 'white',
                            fontWeight: 500,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            '&.Mui-disabled': {
                                background: theme.palette.grey[300],
                                color: theme.palette.grey[500],
                                boxShadow: 'none'
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        Next Phase →
                    </Button>
                </Box>
                
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    p: 3, 
                    backgroundColor: 'white',
                    borderRadius: 4, 
                    border: `1px solid ${isFormValid() ? theme.palette.success.main : theme.palette.grey[400]}`,
                    boxShadow: `0 8px 32px ${alpha(isFormValid() ? theme.palette.success.main : theme.palette.grey[500], 0.12)}`,
                    backdropFilter: 'blur(20px)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    mt: 4
                }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={step.completed || false}
                                onChange={isReadOnly ? undefined : (e) => {
                                    if (!isFormValid() && e.target.checked) {
                                        alert('Please complete all phases before marking as complete.');
                                        return;
                                    }
                                    updateStep({ completed: e.target.checked });
                                }}
                                disabled={isReadOnly || !isFormValid()}
                                sx={{ 
                                    '& .MuiSvgIcon-root': { 
                                        fontSize: 28, 
                                        color: isFormValid() ? theme.palette.success.main : theme.palette.grey[400] 
                                    } 
                                }}
                            />
                        }
                        label=""
                    />
                    <Typography variant="h6" sx={{ 
                        fontWeight: 500, 
                        color: isFormValid() ? theme.palette.success.main : theme.palette.grey[600] 
                    }}>
                        D5 Corrective Actions Complete - Ready for Implementation
                    </Typography>
                    {!isFormValid() && (
                        <Typography variant="body2" sx={{ color: theme.palette.error.main, fontStyle: 'italic' }}>
                            (Complete all phases: Action Planning, Resource Allocation, Risk Assessment, and Verification Planning)
                        </Typography>
                    )}
                </Box>
            </Box>
        </LocalizationProvider>
    );
} 